<script setup lang="ts">
import { faClose } from '@fortawesome/pro-regular-svg-icons'
import { is } from 'valibot'
import { getSrc, videoTimestampSchema } from '~/utils/crystallize/tutorial-video'

definePageMeta({
  name: 'tutorial-video',
})

const route = useRoute<'tutorial-video'>()

const { data, suspense } = useTutorialVideo(computed(() => ensureStringParam(route.params.slug)))

await suspense()

const src = computed(() => getSrc(data.value))

const dialogRef = useTemplateRef('modal')

onMounted(() => {
  if (src.value) {
    dialogRef.value?.showModal()
  }
})

const videoRef = useTemplateRef('video')

const { orientation } = useScreenOrientation()

watch(orientation, (newValue) => {
  if (newValue === 'landscape-primary') {
    if (import.meta.client && !document.fullscreenElement) {
      const v = videoRef.value
      if (v && 'requestFullScreen' in v && typeof v.requestFullScreen === 'function') {
        v.requestFullScreen()
      }
      else if (v && 'webkitRequestFullscreen' in v && typeof v.webkitRequestFullscreen === 'function') {
        v.webkitRequestFullscreen()
      }
    }
  }
})

const controls = useMediaControls(videoRef)

watch([
  controls.playing,
  () => route.query.timestamp,
], ([isPlaying, timestamp]) => {
  if (isPlaying && timestamp && is(videoTimestampSchema, timestamp)) {
    const [minutes = 0, seconds = 0] = timestamp.split(':').map(Number)
    controls.currentTime.value = (minutes * 60) + seconds
    const { timestamp: _removed, ...query } = route.query
    navigateTo({ query })
  }
})

function handleClick(event: Event) {
  // Close the dialog if the backdrop is clicked
  if (event.target === dialogRef.value) {
    dialogRef.value?.close()
  }
}

useSeoMeta({
  title: computed(() => data.value?.components?.title),
  description: computed(() => getTextContent(data.value?.components?.description)),
})

useHead({
  bodyAttrs: {
    class: 'overflow-hidden',
  },
})
</script>

<template>
  <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
  <dialog
    v-if="src"
    ref="modal"
    class="fixed max-h-screen p-[0.6rem] md:p-[0.9rem] w-[calc(100%+2*1.8rem)] backdrop:bg-carbon-500/90 bg-transparent"
    @keydown.esc="dialogRef?.close()"
    @click="handleClick"
    @close="navigateTo({ name: 'tutorial-videos' })"
  >
    <button class="absolute z-10 top-0 right-0 size-8 md:size-12 max-h-[30vh] hover:contrast-75" @click="dialogRef?.close()">
      <Shape variant="octagon" class="fill-moss-200 text-blurple-500" :icon="faClose" />
    </button>
    <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
    <video ref="video" :src="src" controls autoplay class=" max-h-screen rounded-2xl" />
  </dialog>
</template>
